/* eslint-disable react/no-danger */
/* eslint-disable react/jsx-indent */
/* eslint-disable react/jsx-props-no-spreading */
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import * as AppPropTypes from '../../../lib/PropTypes';

import { useThemeClassName } from '../../../contexts/ThemeContext';

import styles from '../../../../styles/displays/slides/question.module.scss';

const propTypes = {
    id: PropTypes.string,
    question: PropTypes.shape({
        questionType: PropTypes.string,
        body: PropTypes.string,
        answers: AppPropTypes.answers,
        choices: PropTypes.arrayOf(PropTypes.shape({})),
        validated: PropTypes.bool,
    }),
    vertical: PropTypes.bool,
};

const defaultProps = {
    id: null,
    question: null,
    vertical: false,
};

function QuestionSlide({ id, question, vertical }) {
    const {
        type = null,
        body = null,
        answers = null,
        choices = null,
        expired = false,
        canAnswer = false,
    } = question || {};

    const themeClassName = useThemeClassName(styles);
    const answer = (answers || []).find((a) => a.id === id) || null;
    const { values = null } = answer || {};

    const isCompleted = expired && !canAnswer;
    const isTrivia = type === 'trivia';
    const showQuestionChoices = body !== null && body[body.length - 1] !== '?';

    const finalChoices = useMemo(
        () =>
            choices !== null
                ? (choices || []).reduce((acc, choice) => {
                      const value = (values || []).find((a) => a.id === choice.id) || null;
                      const words = choice.label.split(' ');
                      const modifiedLabel =
                          words.length === 2 ? `${words[0][0]}. ${words[1]}` : choice.label;
                      acc.push({
                          ...choice,
                          shortLabel: modifiedLabel,
                          count: value !== null ? value.value : 0,
                      });
                      return acc;
                  }, [])
                : (answers || []).map((val) => ({
                      id: val.id,
                      label: val.id,
                      count: val.value || 0,
                  })),
        [choices, answers, values],
    );

    const totalCounts = useMemo(
        () => finalChoices.reduce((sum, choice) => sum + choice.count, 0),
        [finalChoices],
    );

    const finalBody = useMemo(
        () =>
            (body || '')
                .replace('🅫', '<sup>MD</sup>')
                .replace('🅪', '<sup>MC</sup>')
                .replace('™', '<sup>TM</sup>'),
        [body],
    );

    const larger = !showQuestionChoices && !isTrivia && finalBody.length <= 40;

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.isTrivia]: isTrivia,
                    [styles.vertical]: vertical,
                },
                themeClassName,
            ])}
        >
            <div className={styles.question}>
                <div className={styles.badges}>
                    <p className={styles.badge}>{isTrivia ? 'Trivia' : 'Question'}</p>
                    {isCompleted ? (
                        <p className={classNames([styles.badge, styles.completed])}>
                            <FontAwesomeIcon icon={faCheckCircle} />{' '}
                            <FormattedMessage
                                defaultMessage="Completed"
                                description="Badge label"
                            />
                        </p>
                    ) : null}
                </div>
                <h2
                    className={classNames([styles.title, { [styles.larger]: larger }])}
                    dangerouslySetInnerHTML={{ __html: finalBody }}
                />
                <div className={styles.choices}>
                    {showQuestionChoices
                        ? finalChoices.map(({ label }) => (
                              <p className={styles.choiceLabel}>{label}</p>
                          ))
                        : null}
                </div>
                {isCompleted ? (
                    <FontAwesomeIcon className={styles.check} icon={faCheckCircle} />
                ) : null}
            </div>
            <div className={styles.answers}>
                <h2 className={styles.title}>
                    {isTrivia ? (
                        <FormattedMessage defaultMessage="Answers" description="Answers label" />
                    ) : (
                        <FormattedMessage
                            defaultMessage="Predictions"
                            className="Predictions label"
                        />
                    )}
                </h2>
                <div
                    className={classNames([
                        styles.choices,
                        {
                            [styles.grid]: finalChoices.length > 3,
                        },
                    ])}
                >
                    {finalChoices.map(({ id: choiceId, shortLabel, count }) => (
                        <div
                            key={choiceId}
                            className={classNames([
                                styles.choice,
                                {
                                    [styles.valid]: false,
                                    [styles.invalid]: false,
                                },
                            ])}
                        >
                            <div className={styles.bar}>
                                <div
                                    className={styles.barInner}
                                    style={{
                                        height:
                                            count > 0 ? `${(count / totalCounts) * 100}%` : null,
                                    }}
                                >
                                    <p className={styles.label}>{shortLabel}</p>
                                </div>
                            </div>
                            <p className={styles.count}>{count}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

QuestionSlide.propTypes = propTypes;
QuestionSlide.defaultProps = defaultProps;

export default QuestionSlide;
